import { useState } from 'react';
import { Button, Input, Segmented, Space, Table, Tag, message } from 'antd';
import { DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { OwnerType } from '@never2busy/shared';
import { accountsApi, type AccountListItem } from '../../../api/endpoints/accounts';
import { downloadBlob } from '../../../api/downloadBlob';
import { PageHeader } from '../../../components/common/PageHeader';
import { FavoriteToggle } from '../../../components/common/FavoriteToggle';
import { KeywordFilter } from '../../../components/common/KeywordFilter';

type FilterMode = 'all' | 'favorites';

export function AccountsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [keywordIds, setKeywordIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const keywordIdsCsv = keywordIds.length > 0 ? keywordIds.join(',') : undefined;

  const { data, isFetching } = useQuery({
    queryKey: ['accounts', { page, pageSize, search, filter, keywordIdsCsv }],
    queryFn: () =>
      accountsApi.list({
        page,
        pageSize,
        search: search || undefined,
        sortDir: 'asc',
        favoritesOnly: filter === 'favorites' || undefined,
        keywordIds: keywordIdsCsv,
      }),
  });

  const handleExport = (): void => {
    downloadBlob('/accounts/export.csv', 'cuentas.csv', {
      search: search || undefined,
      favoritesOnly: filter === 'favorites' || undefined,
      keywordIds: keywordIdsCsv,
    }).catch(() => message.error('No se pudo exportar'));
  };

  const columns: ColumnsType<AccountListItem> = [
    {
      title: '',
      width: 40,
      align: 'center',
      render: (_, row) => (
        <FavoriteToggle ownerType={OwnerType.ACCOUNT} ownerId={row.id} stopPropagation />
      ),
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      render: (_, row) => <Link to={`/cuentas/${row.id}`}>{row.name}</Link>,
    },
    { title: 'Ciudad', dataIndex: 'city' },
    {
      title: 'Giro',
      dataIndex: 'businessLine',
      render: (v?: string | null) => v ?? '—',
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      render: (v?: string | null) => (v ? <Tag>{v}</Tag> : '—'),
    },
    { title: 'Correo', dataIndex: 'email' },
    { title: 'Teléfono', dataIndex: 'phone' },
  ];

  return (
    <>
      <PageHeader
        title="Cuentas"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Cuentas' }]}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              Exportar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/cuentas/nueva')}
            >
              Nueva cuenta
            </Button>
          </Space>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={[
            { label: 'Todas', value: 'all' },
            { label: '★ Mis favoritos', value: 'favorites' },
          ]}
          value={filter}
          onChange={(v) => {
            setFilter(v as FilterMode);
            setPage(1);
          }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por nombre, correo, teléfono…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <KeywordFilter
          ownerType={OwnerType.ACCOUNT}
          value={keywordIds}
          onChange={(ids) => {
            setKeywordIds(ids);
            setPage(1);
          }}
          style={{ minWidth: 220 }}
        />
      </Space>

      <Table<AccountListItem>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} cuentas`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </>
  );
}
